import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getUserState, saveUserState } from '../services/api';

interface StateContextType {
  sceneState: any; // Can be a more specific type
  updateState: (newState: any) => void;
}

const StateContext = createContext<StateContextType | undefined>(undefined);

export const StateProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [sceneState, setSceneState] = useState({});

  // Load state when user logs in
  useEffect(() => {
    const loadState = async () => {
      if (user) {
        try {
          const state = await getUserState(user.id);
          setSceneState(state || {});
        } catch (error) {
          console.error("Failed to load user state", error);
        }
      } else {
        // Clear state if user logs out
        setSceneState({});
      }
    };
    loadState();
  }, [user]);

  // Update state and save to backend
  const updateState = async (newState: any) => {
    const updatedState = { ...sceneState, ...newState };
    setSceneState(updatedState);
    
    if (user) {
      try {
        await saveUserState(user.id, updatedState);
      } catch (error) {
        console.error("Failed to save user state", error);
      }
    }
  };

  return (
    <StateContext.Provider value={{ sceneState, updateState }}>
      {children}
    </StateContext.Provider>
  );
};

export const useStateContext = () => {
  const context = useContext(StateContext);
  if (context === undefined) {
    throw new Error('useStateContext must be used within a StateProvider');
  }
  return context;
};
